import type { BaseResponse } from "@/api/base"

// --- 与示例 JSON 对齐的枚举字面量（避免 TS enum，使用 const + 联合类型） ---

export const TrackingPageThemeMobileLayout = {
  Hide: "HIDE",
  Stacked: "STACKED",
} as const

export type TrackingPageThemeMobileLayoutId =
  (typeof TrackingPageThemeMobileLayout)[keyof typeof TrackingPageThemeMobileLayout]

export const TrackingPageThemeBottomImageRatio = {
  Auto: "AUTO",
  Portrait: "PORTRAIT",
  Square: "SQUARE",
} as const

export type TrackingPageThemeBottomImageRatioId =
  (typeof TrackingPageThemeBottomImageRatio)[keyof typeof TrackingPageThemeBottomImageRatio]

export const TrackingPageThemeCornerRadius = {
  Rounded: "ROUNDED",
  Sharp: "SHARP",
  Standard: "STANDARD",
} as const

export type TrackingPageThemeCornerRadiusId =
  (typeof TrackingPageThemeCornerRadius)[keyof typeof TrackingPageThemeCornerRadius]

export const TrackingPageThemeLookupWidgetSize = {
  Large: "LARGE",
  Small: "SMALL",
  Standard: "STANDARD",
} as const

export type TrackingPageThemeLookupWidgetSizeId =
  (typeof TrackingPageThemeLookupWidgetSize)[keyof typeof TrackingPageThemeLookupWidgetSize]

export const TrackingPageThemeLookupWidgetPosition = {
  Center: "CENTER",
  Left: "LEFT",
  Right: "RIGHT",
} as const

export type TrackingPageThemeLookupWidgetPositionId =
  (typeof TrackingPageThemeLookupWidgetPosition)[keyof typeof TrackingPageThemeLookupWidgetPosition]

export const TrackingPageThemeContentColumnType = {
  OrderLookupWidget: "order_lookup_widget",
  Image: "image",
} as const

export type TrackingPageThemeContentColumnTypeId =
  (typeof TrackingPageThemeContentColumnType)[keyof typeof TrackingPageThemeContentColumnType]

export const TrackingPageThemeBottomMediaBlockType = {
  Image: "image",
  Video: "video",
} as const

export type TrackingPageThemeBottomMediaBlockTypeId =
  (typeof TrackingPageThemeBottomMediaBlockType)[keyof typeof TrackingPageThemeBottomMediaBlockType]

// --- 通用嵌套结构 ---

export interface TrackingPageThemeMediaImage {
  gid: string
  url: string
  alt: string
}

/** 编辑器内：有图为对象，无图为 null */
export type TrackingPageThemeImageField = TrackingPageThemeMediaImage | null

/** GET 可能为对象或数组；保存时无图须为各字段空字符串的对象，不能为 null 或 [] */
export type TrackingPageThemeImageApiValue =
  | TrackingPageThemeMediaImage
  | TrackingPageThemeMediaImage[]
  | null

export const emptyTrackingPageThemeMediaImage = (): TrackingPageThemeMediaImage => ({
  gid: "",
  url: "",
  alt: "",
})

export interface TrackingPageThemeImageBannerBlock {
  image: TrackingPageThemeImageField
  mobile_layout: TrackingPageThemeMobileLayoutId
}

export interface TrackingPageThemeBottomSectionImageItem {
  type: typeof TrackingPageThemeBottomMediaBlockType.Image
  /** 元素稳定标识：用于拖拽排序时让 draft↔config 往返保持身份不变（空行无 gid 时尤为关键）。
   *  旧数据可能没有此字段，draft 侧需回退到 gid/下标。 */
  id?: string
  enabled: boolean
  image: TrackingPageThemeImageField
  destination_url: string
  open_new_window: boolean
}

export interface TrackingPageThemeBottomSectionVideoItem {
  type: typeof TrackingPageThemeBottomMediaBlockType.Video
  enabled: boolean
  url: string
  alt: string
  auto_play: boolean
  loop_play: boolean
}

export type TrackingPageThemeBottomSectionMediaItem =
  | TrackingPageThemeBottomSectionImageItem
  | TrackingPageThemeBottomSectionVideoItem

export interface TrackingPageThemeBottomSectionContent {
  image_ratio: TrackingPageThemeBottomImageRatioId
  images: TrackingPageThemeBottomSectionImageItem[]
  videos: TrackingPageThemeBottomSectionVideoItem[]
}

// --- Backdrop：content / style ---

export interface TrackingPageThemeBackdropTopSectionContent {
  image_banner: TrackingPageThemeImageBannerBlock
}

export interface TrackingPageThemeBackdropContent {
  top_section: TrackingPageThemeBackdropTopSectionContent
  bottom_section: TrackingPageThemeBottomSectionContent
}

export interface TrackingPageThemeBackdropOrderLookupStyle {
  display: {
    corner_radius: TrackingPageThemeCornerRadiusId
  }
  layout: {
    size: TrackingPageThemeLookupWidgetSizeId
    position: TrackingPageThemeLookupWidgetPositionId
    desktop_bg_color: string
  }
}

export interface TrackingPageThemeBackdropStyle {
  order_lookup_widget: TrackingPageThemeBackdropOrderLookupStyle
}

export interface TrackingPageThemeBackdropThemeConfig {
  content: TrackingPageThemeBackdropContent
  style: TrackingPageThemeBackdropStyle
}

// --- Showcase：columns + bottom ---

export interface TrackingPageThemeShowcaseColumnOrderLookup {
  type: typeof TrackingPageThemeContentColumnType.OrderLookupWidget
  enabled: boolean
}

export interface TrackingPageThemeShowcaseColumnImage {
  type: typeof TrackingPageThemeContentColumnType.Image
  /** 元素稳定标识：用于拖拽排序与逐列编辑/勾选时让 draft↔config 往返保持身份唯一
   *  （多列使用同一张图片时，不能再用 gid 当 id，否则会相互串改）。后端不接受此字段，发送前剥离。 */
  id?: string
  enabled: boolean
  image: TrackingPageThemeImageField
  destination_url: string
  open_new_window: boolean
}

export type TrackingPageThemeShowcaseColumn =
  | TrackingPageThemeShowcaseColumnOrderLookup
  | TrackingPageThemeShowcaseColumnImage

export interface TrackingPageThemeShowcaseContent {
  columns: TrackingPageThemeShowcaseColumn[]
  bottom_section: TrackingPageThemeBottomSectionContent
}

export interface TrackingPageThemeShowcaseOrderLookupStyle {
  display: {
    corner_radius: TrackingPageThemeCornerRadiusId
  }
  /** 示例：`"50%"`，具体格式以后端校验为准 */
  width: string
}

export interface TrackingPageThemeShowcaseStyle {
  order_lookup_widget: TrackingPageThemeShowcaseOrderLookupStyle
}

export interface TrackingPageThemeShowcaseThemeConfig {
  content: TrackingPageThemeShowcaseContent
  style: TrackingPageThemeShowcaseStyle
}

// --- 持久化载荷（GET 返回 / 保存时可作为 `config` 整体） ---

export const TrackingPageTheme = {
  Backdrop: 2,
  Showcase: 3,
} as const

export type TrackingPageThemeId = (typeof TrackingPageTheme)[keyof typeof TrackingPageTheme]

export interface TrackingPageThemeBackdropPersistPayload {
  theme: TrackingPageThemeId
  theme_config: TrackingPageThemeBackdropThemeConfig
}

export interface TrackingPageThemeShowcasePersistPayload {
  theme: TrackingPageThemeId
  theme_config: TrackingPageThemeShowcaseThemeConfig
}

export interface TrackingPageThemeLayoutPersistPayload {
  theme: TrackingPageThemeId
  theme_config: TrackingPageThemeLayoutConfigBody
}

/**
 * 部分店铺会返回内联结构（`content` + `style`），无 `theme_config` 包裹。
 */
export interface TrackingPageThemeLayoutInlinePayload {
  theme: TrackingPageThemeId
  content: TrackingPageThemeBackdropContent | TrackingPageThemeShowcaseContent
  style: TrackingPageThemeBackdropStyle | TrackingPageThemeShowcaseStyle
}

/**
 * 部分店铺会返回 `tracking_page_theme_config` 包裹。
 */
export interface TrackingPageThemeConfigEnvelopeData {
  tracking_page_theme_config: TrackingPageThemeLayoutInlinePayload | TrackingPageThemeLayoutPersistPayload
  updated_at?: string | null
}

/**
 * 仅 `theme_config` 内层（若保存接口只提交该对象，可使用此联合）。
 */
export type TrackingPageThemeLayoutConfigBody =
  | TrackingPageThemeBackdropThemeConfig
  | TrackingPageThemeShowcaseThemeConfig

/**
 * 保存 `tracking_page/theme_config/:theme` 时的请求体：`config` 键内可为完整载荷或仅 `theme_config`（以后端为准）。
 */
export interface TrackingPageSaveThemeConfigRequestBody {
  tracking_page_theme_config: TrackingPageThemeLayoutPersistPayload | TrackingPageThemeLayoutInlinePayload | TrackingPageThemeLayoutConfigBody
}

/** GET 接口业务数据（接口直接返回该对象，而非 `code/data` 包裹） */
export type TrackingPageThemeConfigGetData =
  | TrackingPageThemeConfigEnvelopeData
  | TrackingPageThemeLayoutInlinePayload
  | TrackingPageThemeLayoutPersistPayload

/** GET 响应体 */
export type TrackingPageThemeConfigGetResponse = TrackingPageThemeConfigGetData

/** 保存成功时 `data` 内可能直接为内联载荷，或包裹在 `tracking_page_theme_config` 下 */
export type TrackingPageThemeConfigSaveResponseData =
  | TrackingPageThemeConfigEnvelopeData
  | TrackingPageThemeLayoutInlinePayload
  | TrackingPageThemeLayoutPersistPayload
  | Record<string, unknown>

/** POST / PUT 保存后响应（业务字段以后端为准） */
export interface TrackingPageThemeConfigSaveResponse extends BaseResponse {
  data?: TrackingPageThemeConfigSaveResponseData
}

/** GET preview 接口响应（直接返回，无 code/data 包裹） */
export interface TrackingPageThemePreviewResponse {
  tracking_page_theme_preview: {
    url: string
  }
}

/** 单个主题布局预览项；`id` 与 `ThemeLayoutEnum` 对齐（1 CLASSIC、2 BACKDROP、3 SHOWCASE） */
export interface TrackingPageThemePreview {
  id: number
  name: string
  url: string
}

/** GET preview_list 接口响应（直接返回，无 code/data 包裹） */
export interface TrackingPageThemePreviewListResponse {
  tracking_page_theme_previews: TrackingPageThemePreview[]
}
